import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { empresaService, cidadeService } from '@/services/api';
import { EmpresaFormData, Empresa } from '@/types';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { useQueryInvalidation } from '@/hooks/useQueryInvalidation';
import { ServerSearchSelect } from '@/components/ui/ServerSearchSelect';

const empresaSchema = z.object({
  nome: z.string().min(1, 'Nome é obrigatório'),
  nomeFantasia: z.string().min(1, 'Nome fantasia é obrigatório'),
  cnpj: z.string().optional(),
  cpfAutonomo: z.string().optional(),
  numClassAutonomo: z.string().optional(),
  endereco: z.string().optional(),
  cidadeId: z.number().min(1, 'Cidade é obrigatória'),
  telefone1: z.string().optional(),
  telefone2: z.string().optional(),
  email: z.string().email('Email inválido').optional().or(z.literal('')),
  representante: z.string().optional(),
  cargoRepresentante: z.string().optional(),
});

export const NovaEmpresaPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isEditing = !!id;
  const { handleError, handleSuccess } = useErrorHandler();
  const { invalidateEmpresas } = useQueryInvalidation();

  const [cidadeLabel, setCidadeLabel] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<EmpresaFormData>({
    resolver: zodResolver(empresaSchema),
    defaultValues: {
      cidadeId: 0,
    },
  });

  // Buscar dados da empresa (se editando)
  const { data: empresaData, isLoading: loadingEmpresa, error: empresaError } = useQuery<Empresa>({
    queryKey: ['empresa', id],
    queryFn: () => empresaService.getById(Number(id)),
    enabled: isEditing,
  });

  useEffect(() => {
    if (empresaError) {
      const message = handleError(empresaError as any, 'Erro ao carregar empresa');
      setLoadError(message);
    }
  }, [empresaError, handleError]);

  useEffect(() => {
    if (empresaData) {
      setLoadError(null);
    }
  }, [empresaData]);


  const createEmpresaMutation = useMutation({
    mutationFn: empresaService.create,
    onSuccess: () => {
      invalidateEmpresas();
      handleSuccess('Empresa criada com sucesso!');
      navigate('/empresas');
    },
    onError: (error: any) => {
      handleError(error, 'Erro ao criar empresa');
    },
  });

  const updateEmpresaMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: EmpresaFormData }) =>
      empresaService.update(id, data),
    onSuccess: () => {
      invalidateEmpresas(id);
      handleSuccess('Empresa atualizada com sucesso!');
      navigate('/empresas');
    },
    onError: (error: any) => {
      handleError(error, 'Erro ao atualizar empresa');
    },
  });

  // Preencher formulário com dados existentes (se editando)
  useEffect(() => {
    if (empresaData) {
      setValue('nome', empresaData.nome);
      setValue('nomeFantasia', empresaData.nomeFantasia);
      setValue('cnpj', empresaData.cnpj?.trim() || '');
      setValue('cpfAutonomo', empresaData.cpfAutonomo?.trim() || '');
      setValue('numClassAutonomo', empresaData.numClassAutonomo?.trim() || '');
      setValue('endereco', empresaData.endereco || '');
      setValue('cidadeId', empresaData.cidadeId);
      setValue('telefone1', empresaData.telefone1 || '');
      setValue('telefone2', empresaData.telefone2 || '');
      setValue('email', empresaData.email || '');
      setValue('representante', empresaData.representante || '');
      setValue('cargoRepresentante', empresaData.cargoRepresentante || '');

      // Definir valor inicial para o select de cidade com busca
      if (empresaData.cidade) {
        setCidadeLabel(`${empresaData.cidade.nome} - ${empresaData.cidade.uf}`);
      }
    }
  }, [empresaData, setValue]);

  const onSubmit = async (data: EmpresaFormData) => {
    try {
      setIsSubmitting(true);
      const dataToSend = {
        ...data,
        cnpj: data.cnpj?.trim() || undefined,
        cpfAutonomo: data.cpfAutonomo?.trim() || undefined,
        numClassAutonomo: data.numClassAutonomo?.trim() || undefined,
      };
      if (isEditing) {
        await updateEmpresaMutation.mutateAsync({ id: Number(id), data: dataToSend });
      } else {
        await createEmpresaMutation.mutateAsync(dataToSend);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading state para edição
  if (isEditing && loadError) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Editar Empresa</h1>
          <p className="mt-1 text-sm text-gray-500">Não foi possível carregar as informações da empresa.</p>
        </div>
        <Card>
          <CardHeader>
            <CardTitle>Erro ao carregar dados</CardTitle>
            <CardDescription>{loadError}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/empresas')}>Voltar para lista</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isEditing && loadingEmpresa) {
    return (
      <div className="space-y-6">
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-2 text-gray-500">Carregando dados da empresa...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          {isEditing ? 'Editar Empresa' : 'Nova Empresa'}
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          {isEditing
            ? 'Edite as informações da empresa'
            : 'Cadastre uma nova empresa no sistema'
          }
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Dados da Empresa</CardTitle>
            <CardDescription>
              Informações básicas da empresa
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="nome">Nome da Empresa *</Label>
                <Input
                  id="nome"
                  placeholder="Nome oficial da empresa"
                  {...register('nome')}
                  className={errors.nome ? 'border-error-500' : ''}
                />
                {errors.nome && (
                  <p className="mt-1 text-sm text-error-600">{errors.nome.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="nomeFantasia">Nome Fantasia *</Label>
                <Input
                  id="nomeFantasia"
                  placeholder="Nome fantasia da empresa"
                  {...register('nomeFantasia')}
                  className={errors.nomeFantasia ? 'border-error-500' : ''}
                />
                {errors.nomeFantasia && (
                  <p className="mt-1 text-sm text-error-600">{errors.nomeFantasia.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="cnpj">CNPJ</Label>
                <Input
                  id="cnpj"
                  placeholder="00.000.000/0000-00"
                  {...register('cnpj')}
                  className={errors.cnpj ? 'border-error-500' : ''}
                />
                {errors.cnpj && (
                  <p className="mt-1 text-sm text-error-600">{errors.cnpj.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="contato@empresa.com"
                  {...register('email')}
                  className={errors.email ? 'border-error-500' : ''}
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-error-600">{errors.email.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="telefone1">Telefone Principal</Label>
                <Input
                  id="telefone1"
                  placeholder="(00) 0000-0000"
                  {...register('telefone1')}
                  className={errors.telefone1 ? 'border-error-500' : ''}
                />
                {errors.telefone1 && (
                  <p className="mt-1 text-sm text-error-600">{errors.telefone1.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="telefone2">Telefone Secundário</Label>
                <Input
                  id="telefone2"
                  placeholder="(00) 0000-0000"
                  {...register('telefone2')}
                  className={errors.telefone2 ? 'border-error-500' : ''}
                />
                {errors.telefone2 && (
                  <p className="mt-1 text-sm text-error-600">{errors.telefone2.message}</p>
                )}
              </div>
            </div>

            <div>
              <Label htmlFor="endereco">Endereço</Label>
              <Input
                id="endereco"
                placeholder="Rua, número, bairro"
                {...register('endereco')}
                className={errors.endereco ? 'border-error-500' : ''}
              />
              {errors.endereco && (
                <p className="mt-1 text-sm text-error-600">{errors.endereco.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="cidadeId">Cidade *</Label>
              <ServerSearchSelect
                value={watch('cidadeId') || null}
                selectedLabel={cidadeLabel}
                onChange={(cidadeId, cidade) => {
                  setValue('cidadeId', cidadeId ?? 0);
                  setCidadeLabel(cidade ? `${cidade.nome} - ${cidade.uf}` : '');
                }}
                fetchPage={(params) => cidadeService.list(params)}
                fetchById={cidadeService.getById}
                getOptionLabel={(cidade) => `${cidade.nome} - ${cidade.uf}`}
                queryKey="cidades"
                placeholder="Buscar cidade…"
                error={Boolean(errors.cidadeId)}
              />
              {errors.cidadeId && (
                <p className="mt-1 text-sm text-error-600">{errors.cidadeId.message}</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Representante</CardTitle>
            <CardDescription>
              Dados do representante da empresa
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="representante">Nome do Representante</Label>
                <Input
                  id="representante"
                  placeholder="Nome completo do representante"
                  {...register('representante')}
                  className={errors.representante ? 'border-error-500' : ''}
                />
                {errors.representante && (
                  <p className="mt-1 text-sm text-error-600">{errors.representante.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="cargoRepresentante">Cargo do Representante</Label>
                <Input
                  id="cargoRepresentante"
                  placeholder="Ex: Diretor, Gerente, etc."
                  {...register('cargoRepresentante')}
                  className={errors.cargoRepresentante ? 'border-error-500' : ''}
                />
                {errors.cargoRepresentante && (
                  <p className="mt-1 text-sm text-error-600">{errors.cargoRepresentante.message}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Dados para Autônomo (Opcional)</CardTitle>
            <CardDescription>
              Preencha apenas se for um profissional autônomo
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="cpfAutonomo">CPF do Autônomo</Label>
                <Input
                  id="cpfAutonomo"
                  placeholder="000.000.000-00"
                  {...register('cpfAutonomo')}
                  className={errors.cpfAutonomo ? 'border-error-500' : ''}
                />
                {errors.cpfAutonomo && (
                  <p className="mt-1 text-sm text-error-600">{errors.cpfAutonomo.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="numClassAutonomo">Número de Classificação do Autônomo</Label>
                <Input
                  id="numClassAutonomo"
                  placeholder="Número de classificação"
                  {...register('numClassAutonomo')}
                  className={errors.numClassAutonomo ? 'border-error-500' : ''}
                />
                {errors.numClassAutonomo && (
                  <p className="mt-1 text-sm text-error-600">{errors.numClassAutonomo.message}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex justify-end space-x-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/empresas')}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting || loadingEmpresa}
          >
            {isSubmitting ? 'Salvando...' : (isEditing ? 'Atualizar Empresa' : 'Criar Empresa')}
          </Button>
        </div>
      </form>
    </div>
  );
};
